import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  Container,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  LinearProgress,
  Chip,
  Alert,
  Tooltip,
  Snackbar,
  Card,
  CardContent,
  Stack,
  Divider,
  AppBar,
  Toolbar,
  useMediaQuery
} from '@mui/material';
import {
  Radio as RadioIcon,
  Brightness4 as DarkIcon,
  Brightness7 as LightIcon,
  Settings as SettingsIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  SelectAll as SelectAllIcon,
  Deselect as DeselectIcon,
  Search as SearchIcon,
  Close as ClearIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { format, subDays, startOfMonth, parseISO, isWeekend } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './datepicker.css';
import { fetchShows } from './services/scraper';
import SettingsDialog from './components/SettingsDialog';
import EpisodeSummary from './components/EpisodeSummary';
import { get881903Program } from './services/cr881903';

export function getStationColor(station) {
  if (!station) return 'default';
  if (station.includes('903')) return 'primary';
  if (station.includes('881')) return 'error';
  if (station.includes('香港電台')) return 'success';
  if (station.includes('新城')) return 'secondary';
  if (station.includes('堅離地球') || station.includes('風雲谷')) return 'warning';
  return 'info';
}

function App() {
  // Theme state
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [darkMode, setDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem('radio_dark_mode');
      return saved !== null ? saved === 'true' : prefersDarkMode;
    } catch (e) {
      return prefersDarkMode;
    }
  });

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? 'dark' : 'light',
          primary: {
            main: '#1976d2'
          },
          secondary: {
            main: '#9c27b0'
          },
          background: {
            default: darkMode ? '#121212' : '#f4f6f8',
            paper: darkMode ? '#1e1e1e' : '#ffffff'
          }
        },
        typography: {
          fontFamily: '"Roboto", "Helvetica", "Arial", "Noto Sans TC", sans-serif'
        },
        components: {
          MuiPaper: {
            styleOverrides: {
              root: {
                borderRadius: 12
              }
            }
          },
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: 8,
                textTransform: 'none',
                fontWeight: 600
              }
            }
          }
        }
      }),
    [darkMode]
  );

  useEffect(() => {
    try {
      localStorage.setItem('radio_dark_mode', String(darkMode));
    } catch (e) {
      // ignore
    }
  }, [darkMode]);

  // Settings state
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [concurrency, setConcurrency] = useState(3);

  // Date selection state
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  // Filter states
  const [dayType, setDayType] = useState('all'); // 'all', 'weekday', 'weekend'
  const [stationFilter, setStationFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [mirrorFilter, setMirrorFilter] = useState('all'); // 'all', 'frge', 'ouo', etc.
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' = Old to New (default), 'desc' = New to Old

  // Scraper results & execution state
  const [rawEpisodes, setRawEpisodes] = useState([]);
  const [selectedShows, setSelectedShows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState({
    processed: 0,
    total: 0,
    percentage: 0,
    cachedCount: 0,
    errorCount: 0,
    currentDate: null
  });

  // UI notifications
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  // Abort controller ref
  const abortControllerRef = useRef(null);

  // Quick Date Preset handler
  const setQuickRange = (preset) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let start = today;
    let end = today;

    if (preset === 'today') {
      start = today;
      end = today;
    } else if (preset === 'yesterday') {
      start = subDays(today, 1);
      end = subDays(today, 1);
    } else if (preset === 'past7') {
      start = subDays(today, 6);
      end = today;
    } else if (preset === 'past14') {
      start = subDays(today, 13);
      end = today;
    } else if (preset === 'thisMonth') {
      start = startOfMonth(today);
      end = today;
    }

    setDateRange([start, end]);
  };

  // Scraping handler
  const handleFetchShows = async () => {
    if (!startDate || !endDate) {
      setError('Please select a valid date range.');
      return;
    }

    setLoading(true);
    setError('');
    setRawEpisodes([]);
    setSelectedShows([]);
    setProgress({
      processed: 0,
      total: 0,
      percentage: 0,
      cachedCount: 0,
      errorCount: 0,
      currentDate: null
    });

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const formattedStart = format(startDate, 'yyyy-MM-dd');
    const formattedEnd = format(endDate, 'yyyy-MM-dd');

    try {
      const result = await fetchShows(
        formattedStart,
        formattedEnd,
        (progressData) => {
          setProgress({
            ...progressData.progress,
            currentDate: progressData.currentDate
          });
          if (progressData.rawEpisodes && progressData.rawEpisodes.length > 0) {
            setRawEpisodes(progressData.rawEpisodes);
          }
        },
        {
          signal: controller.signal,
          concurrency
        }
      );

      if (result.rawEpisodes) {
        setRawEpisodes(result.rawEpisodes);
      }
    } catch (err) {
      if (err.message && err.message.includes('cancelled')) {
        setSnackbar({ open: true, message: 'Scraping cancelled.' });
      } else {
        setError(`Failed to fetch shows: ${err.message || 'Unknown network error'}`);
      }
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  };

  const handleCancelScrape = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  // Derive filtered episodes and shows in real time
  const filteredEpisodes = useMemo(() => {
    return rawEpisodes.filter((ep) => {
      // Day Type filter
      if (dayType !== 'all') {
        const epDate = parseISO(ep.date);
        const isWknd = isWeekend(epDate);
        if (dayType === 'weekday' && isWknd) return false;
        if (dayType === 'weekend' && !isWknd) return false;
      }

      // Station filter
      if (stationFilter !== 'all' && ep.station !== stationFilter) {
        return false;
      }

      // Text search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchesName = ep.name && ep.name.toLowerCase().includes(q);
        const matchesShow = ep.showTitle && ep.showTitle.toLowerCase().includes(q);
        const matchesTopic = ep.topic && ep.topic.toLowerCase().includes(q);
        const matchesHost = ep.hosts && ep.hosts.toLowerCase().includes(q);
        if (!matchesName && !matchesShow && !matchesTopic && !matchesHost) {
          return false;
        }
      }

      return true;
    });
  }, [rawEpisodes, dayType, stationFilter, searchQuery]);

  // Aggregate distinct shows from filtered episodes
  const { distinctShows, availableStations } = useMemo(() => {
    const showsMap = new Map();
    const stationsSet = new Set();

    // Collect all available stations across all raw episodes
    rawEpisodes.forEach((ep) => {
      if (ep.station) stationsSet.add(ep.station);
    });

    filteredEpisodes.forEach((ep) => {
      if (!showsMap.has(ep.name)) {
        showsMap.set(ep.name, {
          name: ep.name,
          showTitle: ep.showTitle,
          station: ep.station,
          episodes: [],
          hosts: ep.hosts
        });
      }
      showsMap.get(ep.name).episodes.push(ep);
    });

    // Sort shows by station, then alphabetically
    const sortedShows = Array.from(showsMap.values()).sort((a, b) => {
      if (a.station !== b.station) return a.station.localeCompare(b.station);
      return a.showTitle.localeCompare(b.showTitle);
    });

    return {
      distinctShows: sortedShows,
      availableStations: Array.from(stationsSet).sort()
    };
  }, [filteredEpisodes, rawEpisodes]);

  // Selection handlers
  const handleToggleShow = (name) => {
    setSelectedShows((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const handleSelectAll = () => {
    const allFilteredNames = distinctShows.map((s) => s.name);
    setSelectedShows(allFilteredNames);
  };

  const handleDeselectAll = () => {
    setSelectedShows([]);
  };

  const handleInvertSelection = () => {
    const allFilteredNames = distinctShows.map((s) => s.name);
    setSelectedShows((prev) =>
      allFilteredNames.filter((name) => !prev.includes(name))
    );
  };

  const getEpKey = (ep) => `${ep.name}#${ep.date}`;

  // Candidate episodes from the selected shows and current filters
  const candidateEpisodes = useMemo(() => {
    const selectedSet = new Set(selectedShows);
    const filtered = filteredEpisodes.filter((ep) => selectedSet.has(ep.name));
    return [...filtered].sort((a, b) => {
      // Primary sort: Date (asc: old to new, desc: new to old)
      const dateComparison =
        sortOrder === 'asc'
          ? a.date.localeCompare(b.date)
          : b.date.localeCompare(a.date);
      if (dateComparison !== 0) return dateComparison;

      // Secondary sort: Station, then Show Title
      if (a.station !== b.station) return (a.station || '').localeCompare(b.station || '');
      return (a.showTitle || a.name || '').localeCompare(b.showTitle || b.name || '');
    });
  }, [filteredEpisodes, selectedShows, sortOrder]);

  // Selected episode keys (array of strings)
  const [selectedEpKeys, setSelectedEpKeys] = useState([]);
  const prevCandidateKeysRef = useRef([]);

  // Auto-sync selectedEpKeys whenever candidateEpisodes change:
  // When candidateEpisodes change, select all new episodes by default
  useEffect(() => {
    const currentKeys = candidateEpisodes.map(getEpKey);
    const prevKeys = prevCandidateKeysRef.current;
    const addedKeys = currentKeys.filter((k) => !prevKeys.includes(k));

    setSelectedEpKeys((prevSelected) => {
      const stillValid = prevSelected.filter((k) => currentKeys.includes(k));
      return [...new Set([...stillValid, ...addedKeys])];
    });

    prevCandidateKeysRef.current = currentKeys;
  }, [candidateEpisodes]);

  // Episode selection handlers
  const handleToggleEpisode = (epKey) => {
    setSelectedEpKeys((prev) =>
      prev.includes(epKey) ? prev.filter((k) => k !== epKey) : [...prev, epKey]
    );
  };

  const handleSelectAllEpisodes = () => {
    setSelectedEpKeys(candidateEpisodes.map(getEpKey));
  };

  const handleDeselectAllEpisodes = () => {
    setSelectedEpKeys([]);
  };

  const handleInvertEpisodes = () => {
    setSelectedEpKeys((prev) =>
      candidateEpisodes.map(getEpKey).filter((k) => !prev.includes(k))
    );
  };

  // Compile selected episodes based on user's episode checkboxes
  const selectedEpisodes = useMemo(() => {
    const keySet = new Set(selectedEpKeys);
    return candidateEpisodes.filter((ep) => keySet.has(getEpKey(ep)));
  }, [candidateEpisodes, selectedEpKeys]);

  // Filter links by mirror if preferred
  const getFilteredLinks = (ep) => {
    if (!ep.linkDetails || ep.linkDetails.length === 0) {
      return (ep.links || []).map((url) => ({ url, label: 'Download', mirror: 'other' }));
    }
    if (mirrorFilter === 'all') {
      return ep.linkDetails;
    }
    return ep.linkDetails.filter((ld) => ld.mirror.toLowerCase() === mirrorFilter.toLowerCase());
  };

  // Extract FRGE URLs for selected episodes
  const frgeUrls = useMemo(() => {
    const urls = [];
    selectedEpisodes.forEach((ep) => {
      const details = ep.linkDetails || [];
      if (details.length > 0) {
        details.forEach((ld) => {
          if (ld.mirror === 'frge' || ld.url.includes('frge.top') || ld.url.includes('frge.')) {
            if (!urls.includes(ld.url)) urls.push(ld.url);
          }
        });
      } else {
        (ep.links || []).forEach((u) => {
          if ((u.includes('frge.top') || u.includes('frge.')) && !urls.includes(u)) urls.push(u);
        });
      }
    });
    return urls;
  }, [selectedEpisodes]);

  // Extract OUO URLs for selected episodes
  const ouoUrls = useMemo(() => {
    const urls = [];
    selectedEpisodes.forEach((ep) => {
      const details = ep.linkDetails || [];
      if (details.length > 0) {
        details.forEach((ld) => {
          if (ld.mirror === 'ouo' || ld.url.includes('ouo.io') || ld.url.includes('ouo.press')) {
            if (!urls.includes(ld.url)) urls.push(ld.url);
          }
        });
      } else {
        (ep.links || []).forEach((u) => {
          if ((u.includes('ouo.io') || u.includes('ouo.press')) && !urls.includes(u)) urls.push(u);
        });
      }
    });
    return urls;
  }, [selectedEpisodes]);

  // Extract all URLs for copying/exporting
  const allDownloadUrls = useMemo(() => {
    const urls = [];
    selectedEpisodes.forEach((ep) => {
      const links = getFilteredLinks(ep);
      links.forEach((l) => {
        if (!urls.includes(l.url)) {
          urls.push(l.url);
        }
      });
    });
    return urls;
  }, [selectedEpisodes, mirrorFilter]);

  // Copy FRGE URLs to clipboard
  const handleCopyFrgeUrls = () => {
    if (frgeUrls.length === 0) return;
    navigator.clipboard.writeText(frgeUrls.join('\n'));
    setSnackbar({
      open: true,
      message: `Copied ${frgeUrls.length} FRGE URLs to clipboard!`
    });
  };

  // Copy OUO URLs to clipboard
  const handleCopyOuoUrls = () => {
    if (ouoUrls.length === 0) return;
    navigator.clipboard.writeText(ouoUrls.join('\n'));
    setSnackbar({
      open: true,
      message: `Copied ${ouoUrls.length} OUO URLs to clipboard!`
    });
  };

  // Copy plain URLs to clipboard
  const handleCopyUrlsOnly = () => {
    if (allDownloadUrls.length === 0) return;
    navigator.clipboard.writeText(allDownloadUrls.join('\n'));
    setSnackbar({
      open: true,
      message: `Copied ${allDownloadUrls.length} download URLs to clipboard!`
    });
  };

  // Copy with formatted metadata
  const handleCopyWithTitles = () => {
    if (selectedEpisodes.length === 0) return;
    const lines = [];
    selectedEpisodes.forEach((ep) => {
      const links = getFilteredLinks(ep);
      if (links.length > 0) {
        const titleInfo = `${ep.name} (${ep.date})${ep.topic ? ` - ${ep.topic}` : ''}`;
        lines.push(`== ${titleInfo} ==`);
        links.forEach((l) => {
          lines.push(`${l.label}: ${l.url}`);
        });
        lines.push('');
      }
    });
    navigator.clipboard.writeText(lines.join('\n'));
    setSnackbar({
      open: true,
      message: `Copied ${allDownloadUrls.length} links with titles to clipboard!`
    });
  };

  // Export URLs as text file
  const handleExportTxt = () => {
    if (allDownloadUrls.length === 0) return;
    const content = allDownloadUrls.join('\n');
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `radio_downloads_${format(new Date(), 'yyyyMMdd_HHmm')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setSnackbar({
      open: true,
      message: `Exported ${allDownloadUrls.length} URLs to text file!`
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box className={darkMode ? 'dark-theme' : ''} sx={{ minHeight: '100vh', pb: 8 }}>
        {/* Top Navigation Bar */}
        <AppBar position="static" elevation={1} color="inherit">
          <Toolbar>
            <RadioIcon color="primary" sx={{ mr: 1.5, fontSize: 28 }} />
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
              Radio Show Scraper HK
            </Typography>
            <Tooltip title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
              <IconButton onClick={() => setDarkMode(!darkMode)} color="inherit">
                {darkMode ? <LightIcon /> : <DarkIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Settings & Cache">
              <IconButton onClick={() => setSettingsOpen(true)} color="inherit">
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ mt: 3 }}>
          {/* Main Search & Control Panel */}
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }} elevation={2}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Select Date Range & Scrape
            </Typography>

            {/* Quick Date Presets */}
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', mr: 1 }}>
                Presets:
              </Typography>
              <Chip label="Today" size="small" onClick={() => setQuickRange('today')} clickable />
              <Chip label="Yesterday" size="small" onClick={() => setQuickRange('yesterday')} clickable />
              <Chip label="Past 7 Days" size="small" onClick={() => setQuickRange('past7')} clickable />
              <Chip label="Past 14 Days" size="small" onClick={() => setQuickRange('past14')} clickable />
              <Chip label="This Month" size="small" onClick={() => setQuickRange('thisMonth')} clickable />
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '2fr 1.5fr auto' },
                gap: 2,
                alignItems: 'center'
              }}
            >
              {/* Datepicker */}
              <Box>
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => setDateRange(update)}
                  isClearable={true}
                  placeholderText="Click to select start and end date"
                  className="form-control"
                  wrapperClassName="date-picker-wrapper"
                  dateFormat="yyyy-MM-dd"
                  maxDate={new Date()}
                />
              </Box>

              {/* Day Selection Filter */}
              <FormControl fullWidth size="medium">
                <InputLabel id="day-selection-label">Day Selection</InputLabel>
                <Select
                  labelId="day-selection-label"
                  value={dayType}
                  label="Day Selection"
                  onChange={(e) => setDayType(e.target.value)}
                >
                  <MenuItem value="all">All Days (Everyday)</MenuItem>
                  <MenuItem value="weekday">Mon to Fri (Weekdays)</MenuItem>
                  <MenuItem value="weekend">Sat & Sun (Weekends)</MenuItem>
                </Select>
              </FormControl>

              {/* Fetch / Cancel Button */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleFetchShows}
                  disabled={loading || !startDate || !endDate}
                  sx={{ height: 50, px: 3, minWidth: 140 }}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                >
                  {loading ? 'Scraping...' : 'Fetch Shows'}
                </Button>
                {loading && (
                  <Button variant="outlined" color="error" onClick={handleCancelScrape} sx={{ height: 50 }}>
                    Cancel
                  </Button>
                )}
              </Box>
            </Box>

            {/* Scraping Progress Bar */}
            {loading && (
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Processing dates: {progress.processed} of {progress.total} (
                    {progress.cachedCount > 0 ? `${progress.cachedCount} cached, ` : ''}
                    {progress.percentage}%)
                  </Typography>
                  {progress.currentDate && (
                    <Typography variant="caption" color="primary">
                      Fetching: {progress.currentDate}
                    </Typography>
                  )}
                </Box>
                <LinearProgress variant="determinate" value={progress.percentage} sx={{ height: 8, borderRadius: 4 }} />
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Paper>

          {/* Show Filtering & Selection Section */}
          {(rawEpisodes.length > 0 || (loading && distinctShows.length > 0)) && (
            <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }} elevation={2}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', md: 'center' },
                  gap: 2,
                  mb: 2.5
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  Select Shows ({distinctShows.length} available, {selectedShows.length} selected)
                </Typography>

                {/* Selection Action Buttons */}
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Button size="small" variant="outlined" startIcon={<SelectAllIcon />} onClick={handleSelectAll}>
                    Select All
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DeselectIcon />}
                    onClick={handleDeselectAll}
                    disabled={selectedShows.length === 0}
                  >
                    Clear
                  </Button>
                  <Button size="small" variant="text" onClick={handleInvertSelection}>
                    Invert
                  </Button>
                </Stack>
              </Box>

              {/* Live Filtering Toolbar */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' },
                  gap: 2,
                  mb: 2.5
                }}
              >
                {/* Search query input */}
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Filter shows, hosts, or topics..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchQuery('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }}
                />

                {/* Station Filter Dropdown */}
                <FormControl fullWidth size="small">
                  <InputLabel id="station-filter-label">Filter by Station</InputLabel>
                  <Select
                    labelId="station-filter-label"
                    value={stationFilter}
                    label="Filter by Station"
                    onChange={(e) => setStationFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Radio Stations ({rawEpisodes.length} eps)</MenuItem>
                    {availableStations.map((st) => (
                      <MenuItem key={st} value={st}>
                        {st}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Show Checkbox Grid */}
              {distinctShows.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No shows match your current filters (search: &ldquo;{searchQuery}&rdquo;, station: &ldquo;{stationFilter}&rdquo;).
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                    gap: 1,
                    maxHeight: 380,
                    overflowY: 'auto',
                    p: 1,
                    bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
                    borderRadius: 2
                  }}
                >
                  <FormGroup sx={{ width: '100%', display: 'contents' }}>
                    {distinctShows.map((show) => {
                      const isChecked = selectedShows.includes(show.name);
                      return (
                        <Paper
                          key={show.name}
                          variant="outlined"
                          onClick={() => handleToggleShow(show.name)}
                          sx={{
                            p: 1,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            borderColor: isChecked ? 'primary.main' : 'divider',
                            bgcolor: isChecked
                              ? (t) => (t.palette.mode === 'dark' ? 'rgba(25,118,210,0.15)' : 'rgba(25,118,210,0.06)')
                              : 'inherit',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <Checkbox checked={isChecked} onChange={() => handleToggleShow(show.name)} size="small" />
                          <Box sx={{ ml: 0.5, overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                              <Typography variant="body2" fontWeight={isChecked ? 'bold' : 'normal'} noWrap>
                                {show.showTitle || show.name}
                              </Typography>
                              <Chip
                                label={show.station}
                                size="small"
                                color={getStationColor(show.station)}
                                sx={{ height: 18, fontSize: '0.68rem' }}
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {show.episodes.length} ep{show.episodes.length > 1 ? 's' : ''}
                              {show.hosts ? ` · ${show.hosts}` : ''}
                            </Typography>
                          </Box>
                        </Paper>
                      );
                    })}
                  </FormGroup>
                </Box>
              )}
            </Paper>
          )}

          {/* Results & Download Links Section */}
          {candidateEpisodes.length > 0 && (
            <Paper sx={{ p: { xs: 2, sm: 3 } }} elevation={2}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', md: 'center' },
                  gap: 2,
                  mb: 2.5
                }}
              >
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Download URLs ({selectedEpisodes.length} of {candidateEpisodes.length} episodes selected, {allDownloadUrls.length} links)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select episodes below and copy URLs directly for your preferred download manager
                  </Typography>
                </Box>

                {/* Bulk Action Buttons */}
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CopyIcon />}
                    onClick={handleCopyFrgeUrls}
                    disabled={frgeUrls.length === 0}
                  >
                    Copy FRGE URLs ({frgeUrls.length})
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<CopyIcon />}
                    onClick={handleCopyOuoUrls}
                    disabled={ouoUrls.length === 0}
                  >
                    Copy OUO URLs ({ouoUrls.length})
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CopyIcon />}
                    onClick={handleCopyUrlsOnly}
                    disabled={allDownloadUrls.length === 0}
                  >
                    Copy All URLs ({allDownloadUrls.length})
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CopyIcon />}
                    onClick={handleCopyWithTitles}
                    disabled={allDownloadUrls.length === 0}
                  >
                    Copy with Titles
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportTxt}
                    disabled={allDownloadUrls.length === 0}
                  >
                    Export .TXT
                  </Button>
                </Stack>
              </Box>

              {/* Sub-toolbar: Episode quick selection + Date sorting & Mirror filter */}
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: 2,
                  flexWrap: 'wrap'
                }}
              >
                {/* Episode selection quick buttons */}
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
                    Episodes:
                  </Typography>
                  <Button size="small" variant="outlined" onClick={handleSelectAllEpisodes}>
                    Select All
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleDeselectAllEpisodes}
                    disabled={selectedEpKeys.length === 0}
                  >
                    Clear
                  </Button>
                  <Button size="small" variant="text" onClick={handleInvertEpisodes}>
                    Invert
                  </Button>
                </Stack>

                {/* Date order & Mirror filter */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Date Order:
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <Select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                        <MenuItem value="asc">Old to New (Ascending)</MenuItem>
                        <MenuItem value="desc">New to Old (Descending)</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Mirror:
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <Select value={mirrorFilter} onChange={(e) => setMirrorFilter(e.target.value)}>
                        <MenuItem value="all">All Mirrors (FRGE & OUO)</MenuItem>
                        <MenuItem value="frge">FRGE Only</MenuItem>
                        <MenuItem value="ouo">OUO Only</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Episode Cards */}
              <Stack spacing={1.5}>
                {candidateEpisodes.map((ep, idx) => {
                  const epKey = getEpKey(ep);
                  const isChecked = selectedEpKeys.includes(epKey);
                  const links = getFilteredLinks(ep);
                  const crProg = get881903Program(ep.showTitle || ep.name);

                  return (
                    <Card
                      key={`${epKey}-${idx}`}
                      variant="outlined"
                      sx={{
                        borderColor: isChecked ? 'primary.main' : 'divider',
                        bgcolor: isChecked
                          ? (t) =>
                              t.palette.mode === 'dark'
                                ? 'rgba(25,118,210,0.08)'
                                : 'rgba(25,118,210,0.03)'
                          : 'inherit',
                        transition: 'border-color 0.15s ease, background-color 0.15s ease'
                      }}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        {/* Header: Episode Checkbox, Station, Show Title, Date, Official Link */}
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            gap: 1,
                            mb: 1.5
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Checkbox
                              checked={isChecked}
                              onChange={() => handleToggleEpisode(epKey)}
                              size="small"
                              sx={{ p: 0.5, mr: 0.5 }}
                            />
                            <Chip
                              label={ep.station}
                              size="small"
                              color={getStationColor(ep.station)}
                              sx={{ fontWeight: 'bold' }}
                            />
                            <Typography
                              variant="subtitle1"
                              fontWeight={isChecked ? 'bold' : 'normal'}
                              onClick={() => handleToggleEpisode(epKey)}
                              sx={{ cursor: 'pointer' }}
                            >
                              {ep.showTitle || ep.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ({ep.date})
                            </Typography>
                          </Box>

                          {crProg && (
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<OpenInNewIcon fontSize="inherit" />}
                              href={crProg.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ fontSize: '0.78rem', py: 0.25 }}
                            >
                              881903.com 官方專頁
                            </Button>
                          )}
                        </Box>

                        {/* Half-Hour Summaries (replacing the old description) */}
                        <Box sx={{ mb: 1.5, pl: { sm: 4.5 } }}>
                          <EpisodeSummary episode={ep} />
                        </Box>

                        {/* Download link chips */}
                        <Box sx={{ pl: { sm: 4.5 } }}>
                          {links.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No download links found for this episode.
                            </Typography>
                          ) : (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {links.map((linkItem, linkIdx) => (
                                <Chip
                                  key={linkIdx}
                                  label={linkItem.label}
                                  component="a"
                                  href={linkItem.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  clickable
                                  icon={<OpenInNewIcon fontSize="small" />}
                                  color={linkItem.mirror === 'frge' ? 'primary' : 'secondary'}
                                  variant="outlined"
                                  sx={{ fontWeight: 'medium' }}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </Paper>
          )}
        </Container>

        {/* Settings Dialog */}
        <SettingsDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          concurrency={concurrency}
          setConcurrency={setConcurrency}
        />

        {/* Toast / Snackbar Notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ open: false, message: '' })}
          message={snackbar.message}
        />
      </Box>
    </ThemeProvider>
  );
}

export default App;
